import React, { useState, type FC } from 'react';
import { Check, Copy, ExternalLink, Link, ShieldCheck } from 'lucide-react';
import { withCvShareRef, withOwnerViewMarker } from '@tabbio/utils/analytics/cv-traffic';
import { CardShell } from './card-shell';
import { ApprovalBanner } from './approval-banner';
import type { ToolCardProps } from './types';
import { safeGet } from './types';

export const CVShareCard: FC<ToolCardProps> = ({
  result,
  toolName,
  stage,
  approvalId,
}) => {
  const [copied, setCopied] = useState(false);
  const isAccessGrant = toolName === 'cvAccessGrantCreate';

  if (stage === 'pending_approval' && approvalId) {
    return (
      <CardShell>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Header isAccessGrant={isAccessGrant} />
          <div style={titleStyle}>
            {isAccessGrant ? 'Grant CV access' : 'Create CV share link'}
          </div>
        </div>
        <ApprovalBanner approvalId={approvalId} toolName={toolName} />
      </CardShell>
    );
  }

  if (!result || typeof result !== 'object') return null;

  const cvTitle = safeGet<string>(result, 'cvTitle') || 'CV';
  const shareUrl = safeGet<string>(result, 'shareUrl');
  const inviteUrl = safeGet<string>(result, 'inviteUrl');
  const url = isAccessGrant ? inviteUrl : shareUrl;
  const granteeEmail = safeGet<string>(result, 'granteeEmail');
  const role = safeGet<string>(result, 'role');
  const status = safeGet<string>(result, 'status');
  const allowDownload = safeGet<boolean>(result, 'allowDownload');
  const isTailored = safeGet<boolean>(result, 'isTailored');

  const handleCopy = async () => {
    if (!url) return;
    try {
      // An invite is addressed to one person; only the share link is tagged.
      await navigator.clipboard.writeText(isAccessGrant ? url : withCvShareRef(url, 'copy'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard errors are non-blocking.
    }
  };

  const handleOpen = () => {
    if (!url) return;
    // The owner opening their own share link must not count as a view.
    window.open(isAccessGrant ? url : withOwnerViewMarker(url), '_blank', 'noopener,noreferrer');
  };

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Header isAccessGrant={isAccessGrant} />

        <div style={titleStyle}>{cvTitle}</div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {isTailored ? <Badge label="Tailored" /> : null}
          {isAccessGrant && role ? <Badge label={role} /> : null}
          {status ? <Badge label={status} /> : null}
          {!isAccessGrant && allowDownload ? <Badge label="Downloads enabled" /> : null}
        </div>

        {isAccessGrant ? (
          <div style={bodyStyle}>
            {granteeEmail
              ? `Access ${status === 'pending' ? 'invite' : 'grant'} for ${granteeEmail}.`
              : `Access ${status === 'pending' ? 'invite' : 'grant'} created.`}
          </div>
        ) : (
          <div style={bodyStyle}>
            Share link created for this {isTailored ? 'tailored CV' : 'CV'}.
          </div>
        )}

        {url ? (
          <div style={urlRowStyle}>
            <span style={urlTextStyle}>{url}</span>
            <button type="button" onClick={handleCopy} style={iconButtonStyle} aria-label="Copy link">
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
            <button type="button" onClick={handleOpen} style={iconButtonStyle} aria-label="Open link">
              <ExternalLink size={14} />
            </button>
          </div>
        ) : null}
      </div>
    </CardShell>
  );
};

function Header({ isAccessGrant }: { isAccessGrant: boolean }) {
  const Icon = isAccessGrant ? ShieldCheck : Link;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <Icon size={16} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 500 }}>
        {isAccessGrant ? 'CV access' : 'CV share'}
      </span>
    </div>
  );
}

function Badge({ label }: { label: string }) {
  return (
    <span
      style={{
        fontSize: 11,
        color: 'var(--text-secondary)',
        background: 'rgba(0,0,0,0.04)',
        borderRadius: 9999,
        padding: '2px 8px',
        lineHeight: '16px',
      }}
    >
      {label}
    </span>
  );
}

const titleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--text-primary)',
  lineHeight: '22px',
};

const bodyStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--text-secondary)',
  lineHeight: '20px',
};

const urlRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 10px',
  background: 'rgba(0,0,0,0.04)',
  borderRadius: 8,
  border: '1px solid var(--gem-outline-variant)',
};

const urlTextStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 12,
  fontFamily: 'monospace',
  color: 'var(--text-primary)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const iconButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: 6,
  border: 'none',
  background: 'transparent',
  color: 'var(--text-tertiary)',
  cursor: 'pointer',
  flexShrink: 0,
};
